import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Rating,
  FormControlLabel,
  Checkbox,
  Container,
  Paper,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Business,
  LocationOn,
  Settings,
} from '@mui/icons-material';
import { suppliersAPI } from '../../services/API';
import { useSnackbar } from 'notistack';

export const InvCreateSupplier = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    alternatePhone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'India',
    },
    gstNumber: '',
    panNumber: '',
    paymentTerms: '30_days',
    rating: 3,
    leadTime: 7,
    isActive: true,
    notes: '',
  });

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await suppliersAPI.create(formData);
      enqueueSnackbar('Supplier created successfully!', { variant: 'success' });
      navigate('/hospital/hospital-inventory/suppliers');
    } catch (error) {
      enqueueSnackbar('Error creating supplier', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/hospital/hospital-inventory/suppliers')}
            variant="outlined"
            sx={{ 
              color: 'white', 
              borderColor: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderColor: 'white'
              }
            }}
          >
            Back to Suppliers
          </Button>
        </Box>
        <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
          Add New Supplier
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, mt: 1, fontSize: '1.2rem' }}>
          Create a new supplier profile for your inventory management
        </Typography>
      </Paper>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          {/* Left Column */}
          <Grid item xs={12} lg={6}>
            {/* Basic Information Card */}
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Business sx={{ fontSize: '2rem', color: 'primary.main' }} />
                  <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                    Basic Information
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Supplier Name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      value={formData.contactPerson}
                      onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                      required
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="email"
                      label="Email Address"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      required
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Alternate Phone"
                      value={formData.alternatePhone}
                      onChange={(e) => handleInputChange('alternatePhone', e.target.value)}
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Business Information Card */}
            <Card sx={{ borderRadius: 3, boxShadow: 3, mt: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                  Business Information
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="GST Number"
                      value={formData.gstNumber}
                      onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="PAN Number"
                      value={formData.panNumber}
                      onChange={(e) => handleInputChange('panNumber', e.target.value)}
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} lg={6}>
            {/* Address Information Card */}
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <LocationOn sx={{ fontSize: '2rem', color: 'primary.main' }} />
                  <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                    Address Information
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      value={formData.address.street}
                      onChange={(e) => handleInputChange('address.street', e.target.value)}
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.address.city}
                      onChange={(e) => handleInputChange('address.city', e.target.value)}
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="State"
                      value={formData.address.state}
                      onChange={(e) => handleInputChange('address.state', e.target.value)}
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      value={formData.address.zipCode}
                      onChange={(e) => handleInputChange('address.zipCode', e.target.value)}
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={formData.address.country}
                      onChange={(e) => handleInputChange('address.country', e.target.value)}
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Supplier Settings Card */}
            <Card sx={{ borderRadius: 3, boxShadow: 3, mt: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Settings sx={{ fontSize: '2rem', color: 'primary.main' }} />
                  <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                    Supplier Settings
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Payment Terms"
                      value={formData.paymentTerms}
                      onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 }
                      }}
                    >
                      <MenuItem value="advance" sx={{ fontSize: '1.1rem' }}>Advance</MenuItem>
                      <MenuItem value="7_days" sx={{ fontSize: '1.1rem' }}>7 Days</MenuItem>
                      <MenuItem value="15_days" sx={{ fontSize: '1.1rem' }}>15 Days</MenuItem>
                      <MenuItem value="30_days" sx={{ fontSize: '1.1rem' }}>30 Days</MenuItem>
                      <MenuItem value="45_days" sx={{ fontSize: '1.1rem' }}>45 Days</MenuItem>
                      <MenuItem value="60_days" sx={{ fontSize: '1.1rem' }}>60 Days</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Lead Time (Days)"
                      value={formData.leadTime}
                      onChange={(e) => handleInputChange('leadTime', parseInt(e.target.value))}
                      inputProps={{ min: 1 }}
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography component="legend" sx={{ fontSize: '1.1rem', mb: 1, fontWeight: 'medium' }}>
                      Supplier Rating
                    </Typography>
                    <Rating
                      value={formData.rating}
                      onChange={(event, newValue) => {
                        handleInputChange('rating', newValue);
                      }}
                      size="large"
                      sx={{ fontSize: '2.5rem' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.isActive}
                          onChange={(e) => handleInputChange('isActive', e.target.checked)}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: '1.5rem' } }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                          Active Supplier
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Additional Notes"
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      size="medium"
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mt: 4, 
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              onClick={() => navigate('/hospital/hospital-inventory/suppliers')}
              variant="outlined"
              size="large"
              sx={{ 
                fontSize: '1.1rem',
                px: 4,
                py: 1
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={<Save />}
              sx={{ 
                fontSize: '1.1rem',
                px: 4,
                py: 1,
                minWidth: '160px'
              }}
            >
              {loading ? 'Creating...' : 'Create Supplier'}
            </Button>
          </Box>
        </Paper>
      </form>
    </Container>
  );
};